import React, { useEffect, useState } from "react";
import { Button, Row, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAdd,
  faPencil as PencilIcon,
  faTrash as DeleteIcon,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import Card from "src/components/Cards/Basic";
import FabPlateau from "src/components/UiComponents/Buttons/FabPlateau";
import IconButton from "src/components/IconButton";
import ConfirmDeleteDialog from "src/components/Modal/ConfirmDelete";
import {
  showSuccessToast,
  showValidationErrorToast,
} from "src/components/Toasts";
import axios from "src/config/axios";
import { dateFormatter } from "src/global";
import EditClosedPeriodDialog from "./EditClosedPeriodDialog";

const ClosedPeriodsSettingsPage = () => {
  const { t } = useTranslation("dashboard");
  const [closedPeriods, setClosedPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editedClosedPeriod, setEditedClosedPeriod] = useState(null);
  const [deletedClosedPeriod, setDeletedClosedPeriod] = useState(null);

  useEffect(() => {
    fetchClosedPeriods();
  }, []);

  function fetchClosedPeriods() {
    setLoading(true);

    axios
      .get("/api/v1/closed_periods.json")
      .then(({ data }) => {
        setClosedPeriods(data.entries);
      })
      .catch((error) => {
        showValidationErrorToast(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  function handleSaved() {
    setEditedClosedPeriod(null);
    showSuccessToast();
    fetchClosedPeriods();
  }

  function handleDelete() {
    axios
      .delete(`/api/v1/closed_periods/${deletedClosedPeriod.id}.json`)
      .then(() => {
        setDeletedClosedPeriod(null);
        showSuccessToast(t("settings.business.closed_periods.deleted"));
        fetchClosedPeriods();
      })
      .catch((error) => {
        showValidationErrorToast(error);
      });
  }

  return (
    <>
      <Card
        title={`${t("settings.business.closed_periods.title")} (${
          closedPeriods.length
        })`}
      >
        {loading ? (
          <Spinner
            className="d-block mx-auto"
            animation="border"
            variant="primary"
          />
        ) : (
          <div className="table-responsive">
            <div className="ui-table-container">
              <table
                className="table table-bordered"
                width="100%"
                cellSpacing="0"
              >
                <thead>
                  <tr>
                    <th>
                      {t("settings.business.closed_periods.start_date.label")}
                    </th>
                    <th>
                      {t("settings.business.closed_periods.end_date.label")}
                    </th>
                    <th>{t("settings.business.closed_periods.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {closedPeriods.map((closedPeriod) => (
                    <tr key={closedPeriod.id}>
                      <td>{dateFormatter(closedPeriod.start_date)}</td>
                      <td>{dateFormatter(closedPeriod.end_date)}</td>
                      <td>
                        <IconButton
                          className="mr-3 bg-transparent border-0"
                          textColor="var(--primary)"
                          icon={PencilIcon}
                          onClick={() => setEditedClosedPeriod(closedPeriod)}
                        />
                        <IconButton
                          className="mr-3 bg-transparent border-0"
                          textColor="var(--danger)"
                          icon={DeleteIcon}
                          onClick={() => setDeletedClosedPeriod(closedPeriod)}
                        />
                      </td>
                    </tr>
                  ))}
                  {closedPeriods.length === 0 && (
                    <tr>
                      <td colSpan={3}>
                        {t("common:components.table.noEntries")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <Row className="justify-content-end mr-2 mt-5">
          <FabPlateau>
            <Button
              className="float-right p-2 pl-3 pr-3"
              variant="primary"
              onClick={() => setEditedClosedPeriod({})}
            >
              <FontAwesomeIcon icon={faAdd} />{" "}
              {t("common:components.button.new")}
            </Button>
          </FabPlateau>
        </Row>
      </Card>

      {editedClosedPeriod && (
        <EditClosedPeriodDialog
          closedPeriod={editedClosedPeriod}
          handleClose={() => setEditedClosedPeriod(null)}
          handleSuccess={handleSaved}
        />
      )}

      {deletedClosedPeriod && (
        <ConfirmDeleteDialog
          show={true}
          handleClose={() => setDeletedClosedPeriod(null)}
          handleDelete={handleDelete}
        />
      )}
    </>
  );
};

export default ClosedPeriodsSettingsPage;
